/**
 * A room type's bed numbers on the room types table (batch-29, item R3): the five numbers an organizer saves together,
 * their limits, and the grey numbers suggested for a type that is not set yet. Pure — nothing here reads or writes.
 * A suggestion is only ever shown; it is saved when the organizer chooses "Use suggested" or types and saves.
 */
export const BED_FIELDS = ['beds', 'beds_for_two', 'max_people', 'max_cots', 'max_cribs'] as const;
export type BedField = (typeof BED_FIELDS)[number];
export type BedNumbers = Record<BedField, number>;
/**
 * What a type holds: all five numbers, or none (`null` = not set). `babies_count` null = follow the hotel's setting.
 * `cribs_per_cot_x100` is the type's own "cribs per cot space" in hundredths (200 = two cribs); null = follow the number
 * the event sets for the hotel. `sofa_beds` is how many sofa beds the room has (item BI1): 0 until someone says otherwise.
 * It is shown to guests and changes nothing else: not the people a room takes, not a cot, not a price.
 */
export interface TypeBeds { numbers: BedNumbers | null; babies_count: boolean | null; cribs_per_cot_x100: number | null; sofa_beds: number }
/** The sofa beds of a room type: its own number beside the five, sent only when it is typed. The same limits the database holds. */
export const SOFA_FIELD = 'sofa_beds' as const;
export const SOFA_LABEL = 'Sofa beds';
export const SOFA_LIMIT = { min: 0, max: 10 } as const;
/** The numbers of the room types table, in the order it shows them: the five, with the sofa beds beside the beds. */
export type TableField = BedField | typeof SOFA_FIELD;
export const TABLE_FIELDS: readonly TableField[] = ['beds', SOFA_FIELD, 'beds_for_two', 'max_people', 'max_cots', 'max_cribs'];
/** Cribs in the place of one cot, as the database keeps it: a whole number of hundredths. The one list both pickers offer. */
export const CRIB_RATIOS = [100, 150, 200, 250, 300] as const;
export const CRIB_RATIO_LIMIT = { min: 0, max: 1000 } as const;
/** The choices one picker shows: the list, plus the stored number when it is not on it (never dropped, never rounded). */
export function cribRatioChoices(stored: number | null): number[] {
  return stored === null || (CRIB_RATIOS as readonly number[]).includes(stored) ? [...CRIB_RATIOS] : [...CRIB_RATIOS, stored].sort((a, b) => a - b);
}
/** A type's own settings, each sent only when it is given: left out, the type keeps what it has; null = follow the hotel. `sofaBeds` is a number, never null. */
export interface OwnBedSettings { babiesCount?: boolean | null; cribsPerCot100?: number | null; sofaBeds?: number }
/** What one save sends: the five numbers, and only the own settings that were given. */
export function bedsPayload(numbers: BedNumbers, own: OwnBedSettings = {}): Record<string, number | boolean | null> {
  return {
    ...numbers,
    ...(own.babiesCount === undefined ? {} : { babies_count: own.babiesCount }),
    ...(own.cribsPerCot100 === undefined ? {} : { cribs_per_cot_x100: own.cribsPerCot100 }),
    ...(own.sofaBeds === undefined ? {} : { sofa_beds: own.sofaBeds }),
  };
}

export const BED_LABELS: Record<BedField, string> = { beds: 'Beds', beds_for_two: 'Beds for two', max_people: 'Max people', max_cots: 'Cots', max_cribs: 'Cribs' };
/** The same limits the database holds. */
export const BED_LIMITS: Record<BedField, { min: number; max: number }> = {
  beds: { min: 0, max: 10 }, beds_for_two: { min: 0, max: 10 }, max_people: { min: 1, max: 20 }, max_cots: { min: 0, max: 6 }, max_cribs: { min: 0, max: 10 },
};
export const BEDS_FOR_TWO_OVER = "Beds for two can't be more than beds.";
export const TABLE_LABELS: Record<TableField, string> = { ...BED_LABELS, [SOFA_FIELD]: SOFA_LABEL };
export const TABLE_LIMITS: Record<TableField, { min: number; max: number }> = { ...BED_LIMITS, [SOFA_FIELD]: SOFA_LIMIT };

const clamp = (n: number, f: BedField) => Math.min(BED_LIMITS[f].max, Math.max(BED_LIMITS[f].min, Math.trunc(n)));

/** A stored sofa bed count as the screens read it: a whole number inside the limits, and 0 for anything else (a read made before the column existed). */
export function sofaBedsOf(v: unknown): number {
  return typeof v === 'number' && Number.isInteger(v) && v >= SOFA_LIMIT.min && v <= SOFA_LIMIT.max ? v : 0;
}

/** One row of the database as bed numbers: the five are set together, so one NULL means not set. */
export function asTypeBeds(r: Record<string, unknown>): TypeBeds {
  const whole = BED_FIELDS.every((f) => typeof r[f] === 'number' && Number.isInteger(r[f]));
  return {
    numbers: whole ? (Object.fromEntries(BED_FIELDS.map((f) => [f, r[f] as number])) as BedNumbers) : null,
    babies_count: typeof r.babies_count === 'boolean' ? r.babies_count : null,
    cribs_per_cot_x100: typeof r.cribs_per_cot_x100 === 'number' && Number.isInteger(r.cribs_per_cot_x100)
      && r.cribs_per_cot_x100 >= CRIB_RATIO_LIMIT.min && r.cribs_per_cot_x100 <= CRIB_RATIO_LIMIT.max ? r.cribs_per_cot_x100 : null,
    sofa_beds: sofaBedsOf(r.sofa_beds),
  };
}

/** An extra that is not a bed of the room, with its own number and size. */
const SIZE = 'king|queen|double|full|twin|single';
const EXTRA = new RegExp(`(?:\\b\\d{1,2}\\s+)?(?:\\b(?:${SIZE})(?:[- ]size[d]?)?\\s+)?\\b(?:futons?|murphy(?:\\s+beds?)?|roll-?aways?(?:\\s+beds?)?|cribs?|cots?|day ?beds?|trundles?(?:\\s+beds?)?)\\b`, 'g');

/**
 * The beds in a hotel's bed list ("1 King Bed, 2 Twin Beds"): a king, queen, double or full bed is a bed for two; a twin
 * or single bed is a bed for one. A sofa bed (also "sleeper sofa", "pull-out sofa") is a bed for two, or a bed for one
 * when its entry says single or twin; a bunk bed is two beds for one. A leading number multiplies. Futons, murphy beds,
 * rollaways, cots, cribs, daybeds, trundles and a sofa or couch that is not a bed are left out — the extra alone, with
 * its own number and size ("twin trundle"): "1 Queen Bed with Trundle" keeps its queen bed (item FU1). The words are
 * matched whole: "bedroom" is not a bed and "bunkhouse" is not a bunk. Null when the list names no bed.
 */
export function bedsInList(list: string | null | undefined): { one: number; two: number } | null {
  let one = 0;
  let two = 0;
  for (const part of (list ?? '').toLowerCase().split(/,|;|\band\b|&|\+/)) {
    const words = part.replace(EXTRA, ' ').trim();
    if (!words) continue;
    const n = Number(/^(\d{1,2})\b/.exec(words)?.[1] ?? 1);
    const small = /\b(twin|single)\b/.test(words);
    if (/\bbunks?\b/.test(words)) one += 2 * n;
    else if (/sofa|couch/.test(words)) {
      if (!/\bbeds?\b|sofa-?beds?\b|sleeper|pull[- ]?out/.test(words)) continue;
      if (small) one += n; else two += n;
    } else if (/\b(king|queen|double|full)\b/.test(words)) two += n;
    else if (small) one += n;
  }
  return one + two > 0 ? { one, two } : null;
}

/**
 * The numbers suggested for a type that is not set. From the hotel's bed list when the type is matched to one: its
 * beds, and as Max people the places in them (two in a bed for two) or the largest room of the type, whichever is more.
 * Otherwise from the largest room of the type (`capacity`; a type with no room reads as 2): that many people, in beds
 * for two with one bed for one when the number is odd. Cots and cribs are always suggested as 0.
 */
export function suggestBeds({ bedList, capacity }: { bedList?: string | null; capacity?: number | null }): BedNumbers {
  const cap = typeof capacity === 'number' && Number.isFinite(capacity) && capacity >= 1 ? Math.trunc(capacity) : null;
  const listed = bedsInList(bedList);
  if (listed) {
    const beds = clamp(listed.one + listed.two, 'beds');
    const forTwo = Math.min(clamp(listed.two, 'beds_for_two'), beds);
    return { beds, beds_for_two: forTwo, max_people: clamp(Math.max(beds + forTwo, cap ?? 0), 'max_people'), max_cots: 0, max_cribs: 0 };
  }
  const people = clamp(cap ?? 2, 'max_people');
  const beds = clamp(Math.ceil(people / 2), 'beds');
  return { beds, beds_for_two: Math.min(clamp(Math.floor(people / 2), 'beds_for_two'), beds), max_people: people, max_cots: 0, max_cribs: 0 };
}

/**
 * What a row's five fields come to: a typed field wins, a blank one takes the saved number, else the suggested one.
 * Returns the numbers, or the first thing wrong with them — the field and a sentence.
 */
export function readBedDraft(typed: Partial<Record<BedField, string>>, fallback: BedNumbers): { numbers: BedNumbers } | { field: BedField; error: string } {
  const out = { ...fallback };
  for (const f of BED_FIELDS) {
    const v = (typed[f] ?? '').trim();
    if (v === '') continue;
    const { min, max } = BED_LIMITS[f];
    if (!/^\d{1,2}$/.test(v) || Number(v) < min || Number(v) > max) return { field: f, error: `${BED_LABELS[f]}: enter a whole number from ${min} to ${max}.` };
    out[f] = Number(v);
  }
  if (out.beds_for_two > out.beds) return { field: 'beds_for_two', error: BEDS_FOR_TWO_OVER };
  return { numbers: out };
}

/**
 * The sofa beds a row's field comes to: blank keeps the saved number (nothing is sent); a typed whole number inside the
 * limits is the new one. Returns `null` for "nothing to send", the number, or what is wrong with it.
 */
export function readSofaDraft(typed: string | undefined, saved: number): { sofaBeds: number | null } | { field: typeof SOFA_FIELD; error: string } {
  const v = (typed ?? '').trim();
  if (v === '') return { sofaBeds: null };
  if (!/^\d{1,2}$/.test(v) || Number(v) < SOFA_LIMIT.min || Number(v) > SOFA_LIMIT.max) return { field: SOFA_FIELD, error: `${SOFA_LABEL}: enter a whole number from ${SOFA_LIMIT.min} to ${SOFA_LIMIT.max}.` };
  return { sofaBeds: Number(v) === saved ? null : Number(v) };
}

export const sameBeds = (a: BedNumbers, b: BedNumbers) => BED_FIELDS.every((f) => a[f] === b[f]);
